import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import {
    DollarSign, TrendingUp, AlertCircle, Download, Loader2, Briefcase,
    HardHat, Users2, Receipt, CreditCard, ArrowDownToLine, X, CheckCircle2,
} from "lucide-react";
import { api, formatApiError, TOKEN_KEY } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import EmptyState from "../components/EmptyState";
import dict from "../i18n/es-MX";

const fmtMXN = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 }).format(n || 0);
const fmtPct = (n) => `${(n || 0).toFixed(1)}%`;

// ---------------------------------------------------------------------------
// Date range presets
// ---------------------------------------------------------------------------

function presetRange(key) {
    const today = new Date();
    const y = today.getFullYear(), m = today.getMonth();
    if (key === "this_month") {
        return { from: new Date(y, m, 1).toISOString().slice(0, 10), to: today.toISOString().slice(0, 10) };
    }
    if (key === "last_month") {
        const lm = new Date(y, m - 1, 1);
        const lme = new Date(y, m, 0);
        return { from: lm.toISOString().slice(0, 10), to: lme.toISOString().slice(0, 10) };
    }
    if (key === "ytd") {
        return { from: new Date(y, 0, 1).toISOString().slice(0, 10), to: today.toISOString().slice(0, 10) };
    }
    if (key === "last_6m") {
        const from = new Date(y, m - 5, 1);
        return { from: from.toISOString().slice(0, 10), to: today.toISOString().slice(0, 10) };
    }
    return { from: "", to: "" };
}

function DateRangeBar({ value, onChange }) {
    const [preset, setPreset] = useState("last_6m");
    const presets = [
        { k: "this_month", l: dict.finance.range.thisMonth },
        { k: "last_month", l: dict.finance.range.lastMonth },
        { k: "ytd", l: dict.finance.range.ytd },
        { k: "last_6m", l: dict.finance.range.last6m },
        { k: "custom", l: dict.finance.range.custom },
    ];
    useEffect(() => {
        if (preset !== "custom") onChange(presetRange(preset));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preset]);
    return (
        <div className="flex flex-wrap items-end gap-2">
            {presets.map(p => (
                <button key={p.k} type="button" onClick={() => setPreset(p.k)}
                    data-testid={`range-preset-${p.k}`}
                    className={`h-9 px-3 rounded-lg text-xs uppercase tracking-wider border transition ${preset === p.k ? "border-brand-orange bg-brand-orange/10 text-brand-orange" : "border-slate-700 text-slate-300 hover:text-slate-100"}`}>
                    {p.l}
                </button>
            ))}
            {preset === "custom" && (
                <>
                    <input type="date" value={value.from} onChange={e => onChange({ ...value, from: e.target.value })}
                        data-testid="range-from" className="h-9 px-2 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                    <span className="text-slate-500">→</span>
                    <input type="date" value={value.to} onChange={e => onChange({ ...value, to: e.target.value })}
                        data-testid="range-to" className="h-9 px-2 rounded-lg bg-slate-950 border border-slate-700 text-sm"/>
                </>
            )}
        </div>
    );
}

function Kpi({ label, value, accent = "text-slate-50", testid }) {
    return (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5" data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
            <div className={`mt-3 font-display text-3xl font-extrabold ${accent}`}>{value}</div>
        </div>
    );
}

function LineChart({ data, width = 720, height = 200 }) {
    // data: [{period, revenue, costs, net}]
    if (!data || data.length === 0) {
        return <div className="text-sm text-slate-500 py-8 text-center" data-testid="chart-empty">{dict.finance.chartEmpty}</div>;
    }
    const padding = { top: 10, right: 20, bottom: 28, left: 60 };
    const w = width - padding.left - padding.right;
    const h = height - padding.top - padding.bottom;
    const xs = data.length > 1 ? data.map((_, i) => (i / (data.length - 1)) * w + padding.left) : [padding.left + w / 2];
    const allY = data.flatMap(d => [d.revenue, d.costs, d.net]);
    const minY = Math.min(0, ...allY);
    const maxY = Math.max(1, ...allY);
    const py = v => padding.top + h - ((v - minY) / (maxY - minY)) * h;
    const series = [
        { key: "revenue", color: "#34d399", label: dict.finance.legendRevenue },
        { key: "costs", color: "#fb7185", label: dict.finance.legendCosts },
        { key: "net", color: "#fb923c", label: dict.finance.legendNet },
    ];
    return (
        <div data-testid="chart-monthly">
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
                {/* axes */}
                <line x1={padding.left} y1={padding.top} x2={padding.left} y2={padding.top + h} stroke="#1e293b" strokeWidth={1}/>
                <line x1={padding.left} y1={padding.top + h} x2={padding.left + w} y2={padding.top + h} stroke="#1e293b" strokeWidth={1}/>
                {/* zero line if applicable */}
                {minY < 0 && (
                    <line x1={padding.left} y1={py(0)} x2={padding.left + w} y2={py(0)} stroke="#475569" strokeDasharray="4 4" strokeWidth={1}/>
                )}
                {series.map(s => {
                    const d = data.map((d, i) => `${i === 0 ? "M" : "L"}${xs[i].toFixed(1)},${py(d[s.key]).toFixed(1)}`).join(" ");
                    return (
                        <g key={s.key}>
                            <path d={d} fill="none" stroke={s.color} strokeWidth={2}/>
                            {data.map((d, i) => (
                                <circle key={i} cx={xs[i]} cy={py(d[s.key])} r={3} fill={s.color}/>
                            ))}
                        </g>
                    );
                })}
                {/* x-labels */}
                {data.map((d, i) => (
                    <text key={i} x={xs[i]} y={padding.top + h + 16} textAnchor="middle" fontSize={10} fill="#94a3b8">
                        {d.period.slice(5)}
                    </text>
                ))}
            </svg>
            <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-400">
                {series.map(s => (
                    <span key={s.key} className="inline-flex items-center gap-1.5">
                        <span className="inline-block w-3 h-1.5 rounded" style={{ background: s.color }}/>
                        {s.label}
                    </span>
                ))}
            </div>
        </div>
    );
}

function ExportButton({ url, testid }) {
    const [downloading, setDownloading] = useState(false);
    const onClick = async () => {
        try {
            setDownloading(true);
            const token = localStorage.getItem(TOKEN_KEY);
            const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${url}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!r.ok) throw new Error("download failed");
            const blob = await r.blob();
            const disp = r.headers.get("Content-Disposition") || "";
            const m = disp.match(/filename="([^"]+)"/);
            const filename = (m && m[1]) || "flotilla-mx-reporte.xlsx";
            const objUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objUrl; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(objUrl);
        } catch (e) {
            console.error(e);
        } finally { setDownloading(false); }
    };
    return (
        <button type="button" onClick={onClick} data-testid={testid} disabled={downloading}
            className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs uppercase tracking-wider font-semibold disabled:opacity-60">
            {downloading ? <Loader2 size={14} className="animate-spin"/> : <Download size={14}/>}
            {dict.finance.exportXlsx}
        </button>
    );
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

function ResumenTab({ range }) {
    const [data, setData] = useState(null); const [loading, setLoading] = useState(true); const [err, setErr] = useState("");
    useEffect(() => {
        if (!range.from || !range.to) return;
        setLoading(true); setErr("");
        api.get(`/finance/income-statement?scope=fleet&from=${range.from}&to=${range.to}`)
            .then(r => setData(r.data)).catch(e => setErr(formatApiError(e))).finally(() => setLoading(false));
    }, [range.from, range.to]);
    if (loading) return <div className="text-center py-12"><Loader2 className="animate-spin inline text-slate-500"/></div>;
    if (err) return <div className="text-red-300 text-sm">{err}</div>;
    if (!data) return null;
    const s = data.summary;
    return (
        <div className="space-y-6" data-testid="resumen-tab">
            <div className="flex justify-end">
                <ExportButton url={`/finance/export/income-statement.xlsx?scope=fleet&from=${range.from}&to=${range.to}`} testid="export-summary"/>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Kpi testid="kpi-revenue" label={dict.finance.kpi.revenue} value={fmtMXN(s.revenue_mxn)} accent="text-emerald-300"/>
                <Kpi testid="kpi-total-costs" label={dict.finance.kpi.costs} value={fmtMXN(s.total_costs_mxn)} accent="text-rose-300"/>
                <Kpi testid="kpi-net-profit" label={dict.finance.kpi.netProfit}
                    value={fmtMXN(s.net_profit_mxn)}
                    accent={s.net_profit_mxn >= 0 ? "text-emerald-300" : "text-rose-300"}/>
                <Kpi testid="kpi-margin" label={dict.finance.kpi.margin} value={fmtPct(s.margin_pct)}
                    accent={s.margin_pct >= 0 ? "text-emerald-300" : "text-rose-300"}/>
            </div>
            <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
                <h2 className="font-display text-lg font-bold mb-3">{dict.finance.monthlyTrend}</h2>
                <LineChart data={data.by_period}/>
            </section>
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
                    <h2 className="font-display text-lg font-bold mb-3">{dict.finance.byCategory}</h2>
                    <table className="w-full text-sm">
                        <tbody>
                            {[
                                ["operator_pay_external", dict.finance.cat.operatorPay],
                                ["fuel", dict.finance.cat.fuel],
                                ["maintenance", dict.finance.cat.maintenance],
                                ["financing", dict.finance.cat.financing],
                                ["operator_salaries_fixed", dict.finance.cat.salaries],
                                ["tolls", dict.finance.cat.tolls],
                                ["general_expenses", dict.finance.cat.general],
                            ].map(([k, l]) => (
                                <tr key={k} className="border-b border-slate-800 last:border-0" data-testid={`cat-${k}`}>
                                    <td className="py-2 text-slate-300">{l}</td>
                                    <td className="py-2 text-right font-mono">{fmtMXN(data.by_category[k])}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="border-t-2 border-slate-700"><td className="py-2 font-bold">{dict.finance.totalCosts}</td><td className="py-2 text-right font-bold font-mono text-rose-300">{fmtMXN(s.total_costs_mxn)}</td></tr>
                        </tfoot>
                    </table>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
                    <h2 className="font-display text-lg font-bold mb-3">{dict.finance.internalVsExternal}</h2>
                    <table className="w-full text-sm">
                        <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500"><th className="text-left py-1"></th><th className="text-right py-1">{dict.finance.internal}</th><th className="text-right py-1">{dict.finance.external}</th></tr></thead>
                        <tbody>
                            <tr className="border-t border-slate-800"><td className="py-2">{dict.finance.kpi.revenue}</td><td className="py-2 text-right font-mono">{fmtMXN(s.internal.revenue_mxn)}</td><td className="py-2 text-right font-mono">{fmtMXN(s.external.revenue_mxn)}</td></tr>
                            <tr className="border-t border-slate-800"><td className="py-2">{dict.finance.kpi.costs}</td><td className="py-2 text-right font-mono">{fmtMXN(s.internal.costs_mxn)}</td><td className="py-2 text-right font-mono">{fmtMXN(s.external.costs_mxn)}</td></tr>
                            <tr className="border-t-2 border-slate-700"><td className="py-2 font-bold">{dict.finance.kpi.netProfit}</td><td className={`py-2 text-right font-mono font-bold ${s.internal.net_mxn >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{fmtMXN(s.internal.net_mxn)}</td><td className={`py-2 text-right font-mono font-bold ${s.external.net_mxn >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{fmtMXN(s.external.net_mxn)}</td></tr>
                        </tbody>
                    </table>
                </div>
            </section>
            <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
                <h2 className="font-display text-lg font-bold mb-3">{dict.finance.topJobs}</h2>
                <table className="w-full text-sm">
                    <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500"><th className="text-left py-2">{dict.finance.code}</th><th className="text-left py-2">{dict.finance.clientLabel}</th><th className="text-left py-2">{dict.finance.cls}</th><th className="text-right py-2">{dict.finance.revenue}</th><th className="text-right py-2">{dict.finance.opPay}</th><th className="text-right py-2">{dict.finance.gross}</th></tr></thead>
                    <tbody>
                        {data.by_job.slice(0, 5).map(j => {
                            const gross = (j.revenue_mxn || 0) - (j.operator_pay_mxn || 0);
                            return (
                                <tr key={j.id} className="border-t border-slate-800" data-testid={`top-job-${j.code}`}>
                                    <td className="py-2 font-mono text-xs">{j.code}</td>
                                    <td className="py-2">{j.client_name || "—"}</td>
                                    <td className="py-2 text-xs uppercase tracking-wider text-slate-400">{j.classification}</td>
                                    <td className="py-2 text-right font-mono">{fmtMXN(j.revenue_mxn)}</td>
                                    <td className="py-2 text-right font-mono text-rose-300">{fmtMXN(j.operator_pay_mxn)}</td>
                                    <td className={`py-2 text-right font-mono font-bold ${gross >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{fmtMXN(gross)}</td>
                                </tr>
                            );
                        })}
                        {data.by_job.length === 0 && (
                            <tr><td colSpan={6} className="py-8 text-center text-slate-500 text-sm" data-testid="no-jobs">{dict.finance.noJobsInRange}</td></tr>
                        )}
                    </tbody>
                </table>
            </section>
        </div>
    );
}

function AssetTab({ range }) {
    const [assets, setAssets] = useState([]); const [assetId, setAssetId] = useState("");
    const [data, setData] = useState(null); const [loading, setLoading] = useState(false);
    useEffect(() => { api.get("/assets").then(r => { setAssets(r.data); if (r.data[0]) setAssetId(r.data[0].id); }); }, []);
    useEffect(() => {
        if (!assetId || !range.from) return;
        setLoading(true);
        api.get(`/finance/asset-pnl?asset_id=${assetId}&from=${range.from}&to=${range.to}`)
            .then(r => setData(r.data)).finally(() => setLoading(false));
    }, [assetId, range.from, range.to]);
    return (
        <div className="space-y-5" data-testid="asset-tab">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <select value={assetId} onChange={e => setAssetId(e.target.value)} data-testid="asset-select"
                    className="h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm min-w-[260px]">
                    {assets.map(a => <option key={a.id} value={a.id}>{a.nickname} ({a.kind})</option>)}
                </select>
                {assetId && range.from && (
                    <ExportButton url={`/finance/export/income-statement.xlsx?scope=asset&scope_id=${assetId}&from=${range.from}&to=${range.to}`} testid="export-asset"/>
                )}
            </div>
            {loading || !data ? <div className="text-center py-12"><Loader2 className="animate-spin inline text-slate-500"/></div> : (
                <>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <Kpi label={dict.finance.kpi.revenue} value={fmtMXN(data.summary.revenue_mxn)} accent="text-emerald-300"/>
                        <Kpi label={dict.finance.kpi.costs} value={fmtMXN(data.summary.total_costs_mxn)} accent="text-rose-300"/>
                        <Kpi label={dict.finance.kpi.netProfit} value={fmtMXN(data.summary.net_profit_mxn)} accent={data.summary.net_profit_mxn >= 0 ? "text-emerald-300" : "text-rose-300"}/>
                        <Kpi label={dict.finance.kpi.margin} value={fmtPct(data.summary.margin_pct)}/>
                    </div>
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
                        <h2 className="font-display text-lg font-bold mb-3">{dict.finance.byCategory}</h2>
                        <table className="w-full text-sm">
                            <tbody>
                                {Object.entries(data.by_category).map(([k, v]) => (
                                    <tr key={k} className="border-b border-slate-800 last:border-0">
                                        <td className="py-2 text-slate-300">{dict.finance.cat[k === "operator_pay_external" ? "operatorPay" : k === "operator_salaries_fixed" ? "salaries" : k === "general_expenses" ? "general" : k] || k}</td>
                                        <td className="py-2 text-right font-mono">{fmtMXN(v)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

function OperatorTab({ range }) {
    const [ops, setOps] = useState([]); const [opId, setOpId] = useState("");
    const [data, setData] = useState(null); const [loading, setLoading] = useState(false);
    useEffect(() => { api.get("/operators").then(r => { setOps(r.data); if (r.data[0]) setOpId(r.data[0].id); }); }, []);
    useEffect(() => {
        if (!opId || !range.from) return;
        setLoading(true);
        api.get(`/finance/operator-earnings?operator_id=${opId}&from=${range.from}&to=${range.to}`)
            .then(r => setData(r.data)).finally(() => setLoading(false));
    }, [opId, range.from, range.to]);
    return (
        <div className="space-y-5" data-testid="operator-tab">
            <select value={opId} onChange={e => setOpId(e.target.value)} data-testid="operator-select"
                className="h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm min-w-[260px]">
                {ops.map(o => <option key={o.id} value={o.id}>{o.full_name}</option>)}
            </select>
            {loading || !data ? <div className="text-center py-12"><Loader2 className="animate-spin inline text-slate-500"/></div> : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Kpi testid="kpi-base-salary" label={dict.finance.opEarn.baseSalary} value={fmtMXN(data.summary.base_salary_total_mxn)}/>
                        <Kpi testid="kpi-external-pay" label={dict.finance.opEarn.externalPay} value={fmtMXN(data.summary.external_pay_total_mxn)}/>
                        <Kpi testid="kpi-grand-total" label={dict.finance.opEarn.grandTotal} value={fmtMXN(data.summary.grand_total_mxn)} accent="text-brand-orange"/>
                    </div>
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
                        <h2 className="font-display text-lg font-bold mb-3">{dict.finance.opEarn.byJob}</h2>
                        <table className="w-full text-sm">
                            <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500"><th className="text-left py-2">{dict.finance.code}</th><th className="text-left py-2">{dict.finance.startDate}</th><th className="text-right py-2">Base/día</th><th className="text-right py-2">Días</th><th className="text-right py-2">Comisión %</th><th className="text-right py-2">Bono</th><th className="text-right py-2">Total</th></tr></thead>
                            <tbody>
                                {data.by_job.map(r => (
                                    <tr key={r.job_id} className="border-t border-slate-800" data-testid={`op-job-${r.code}`}>
                                        <td className="py-2 font-mono text-xs">{r.code}</td>
                                        <td className="py-2 text-slate-400">{r.start_date}</td>
                                        <td className="py-2 text-right font-mono">{fmtMXN(r.base_per_day_mxn)}</td>
                                        <td className="py-2 text-right font-mono">{r.days}</td>
                                        <td className="py-2 text-right font-mono">{r.commission_pct}%</td>
                                        <td className="py-2 text-right font-mono">{fmtMXN(r.bonus_fixed_mxn)}</td>
                                        <td className="py-2 text-right font-mono text-emerald-300 font-bold">{fmtMXN(r.computed_total_mxn)}</td>
                                    </tr>
                                ))}
                                {data.by_job.length === 0 && (
                                    <tr><td colSpan={7} className="py-8 text-center text-slate-500 text-sm">{dict.finance.opEarn.noExternal}</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

function ClientTab({ range }) {
    const [clients, setClients] = useState([]); const [cid, setCid] = useState("");
    const [data, setData] = useState(null); const [loading, setLoading] = useState(false);
    useEffect(() => { api.get("/clients").then(r => { setClients(r.data); if (r.data[0]) setCid(r.data[0].id); }); }, []);
    useEffect(() => {
        if (!cid || !range.from) return;
        setLoading(true);
        api.get(`/finance/client-summary?client_id=${cid}&from=${range.from}&to=${range.to}`)
            .then(r => setData(r.data)).finally(() => setLoading(false));
    }, [cid, range.from, range.to]);
    return (
        <div className="space-y-5" data-testid="client-tab">
            <select value={cid} onChange={e => setCid(e.target.value)} data-testid="client-select"
                className="h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm min-w-[260px]">
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {loading || !data ? <div className="text-center py-12"><Loader2 className="animate-spin inline text-slate-500"/></div> : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Kpi testid="kpi-billed" label={dict.finance.client.totalBilled} value={fmtMXN(data.summary.total_billed_mxn)}/>
                        <Kpi testid="kpi-paid" label={dict.finance.client.totalPaid} value={fmtMXN(data.summary.total_paid_mxn)} accent="text-emerald-300"/>
                        <Kpi testid="kpi-outstanding" label={dict.finance.client.outstanding} value={fmtMXN(data.summary.outstanding_mxn)} accent="text-amber-300"/>
                    </div>
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
                        <h2 className="font-display text-lg font-bold mb-3">{dict.finance.client.jobs}</h2>
                        <table className="w-full text-sm">
                            <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500"><th className="text-left py-2">{dict.finance.code}</th><th className="text-left py-2">{dict.finance.startDate}</th><th className="text-right py-2">{dict.finance.revenue}</th><th className="text-right py-2">{dict.finance.client.paid}</th><th className="text-left py-2">{dict.finance.client.payStatus}</th></tr></thead>
                            <tbody>
                                {data.jobs.map(j => (
                                    <tr key={j.id} className="border-t border-slate-800">
                                        <td className="py-2 font-mono text-xs">{j.code}</td>
                                        <td className="py-2 text-slate-400">{j.start_date}</td>
                                        <td className="py-2 text-right font-mono">{fmtMXN(j.revenue_mxn)}</td>
                                        <td className="py-2 text-right font-mono">{fmtMXN(j.amount_paid_mxn)}</td>
                                        <td className="py-2 text-xs uppercase tracking-wider text-slate-400">{j.payment_status}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {data.materials_moved.length > 0 && (
                        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
                            <h2 className="font-display text-lg font-bold mb-3">{dict.finance.client.materialsMoved}</h2>
                            <table className="w-full text-sm">
                                <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500"><th className="text-left py-2">{dict.finance.client.material}</th><th className="text-right py-2">{dict.finance.client.totalQty}</th></tr></thead>
                                <tbody>
                                    {data.materials_moved.map(m => (
                                        <tr key={m.material_id} className="border-t border-slate-800">
                                            <td className="py-2">{m.material_name}</td>
                                            <td className="py-2 text-right font-mono">{m.total_qty.toLocaleString("es-MX")} {m.unit}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function ReceivablesTab() {
    const [status, setStatus] = useState("all");
    const [data, setData] = useState(null); const [loading, setLoading] = useState(true);
    const [payJob, setPayJob] = useState(null);
    const reload = useCallback(() => {
        setLoading(true);
        api.get(`/finance/receivables?status=${status}`).then(r => setData(r.data)).finally(() => setLoading(false));
    }, [status]);
    useEffect(() => { reload(); }, [reload]);
    return (
        <div className="space-y-5" data-testid="receivables-tab">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex gap-2">
                    {[{ k: "all", l: dict.common.all }, { k: "pending", l: dict.finance.recv.pending }, { k: "partial", l: dict.finance.recv.partial }].map(o => (
                        <button key={o.k} type="button" onClick={() => setStatus(o.k)}
                            data-testid={`recv-filter-${o.k}`}
                            className={`h-9 px-3 rounded-lg text-xs uppercase tracking-wider border transition ${status === o.k ? "border-brand-orange bg-brand-orange/10 text-brand-orange" : "border-slate-700 text-slate-300 hover:text-slate-100"}`}>
                            {o.l}
                        </button>
                    ))}
                </div>
                <ExportButton url={`/finance/export/receivables.xlsx?status=${status}`} testid="export-receivables"/>
            </div>
            {loading || !data ? <div className="text-center py-12"><Loader2 className="animate-spin inline text-slate-500"/></div> : (
                <>
                    <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-5">
                        <div className="text-[10px] uppercase tracking-wider text-amber-200/80">{dict.finance.recv.totalOutstanding}</div>
                        <div className="font-display text-4xl font-extrabold text-amber-200 mt-2" data-testid="recv-total">{fmtMXN(data.total_outstanding_mxn)}</div>
                        <div className="text-xs text-amber-200/70 mt-1">{data.count} {dict.finance.recv.jobsWord}</div>
                    </div>
                    <table className="w-full text-sm rounded-2xl border border-slate-800 overflow-hidden" data-testid="recv-table">
                        <thead className="bg-slate-900/80 text-xs uppercase text-slate-400">
                            <tr><th className="text-left p-3">{dict.finance.code}</th><th className="text-left p-3">{dict.finance.clientLabel}</th><th className="text-left p-3">{dict.finance.startDate}</th><th className="text-right p-3">{dict.finance.revenue}</th><th className="text-right p-3">{dict.finance.client.paid}</th><th className="text-right p-3">{dict.finance.recv.outstanding}</th><th className="text-right p-3">{dict.finance.recv.daysOpen}</th><th className="text-right p-3"></th></tr>
                        </thead>
                        <tbody>
                            {data.rows.map(j => (
                                <tr key={j.id} className="border-t border-slate-800" data-testid={`recv-row-${j.code}`}>
                                    <td className="p-3 font-mono text-xs">{j.code}</td>
                                    <td className="p-3">{j.client_name}</td>
                                    <td className="p-3 text-slate-400">{j.start_date}</td>
                                    <td className="p-3 text-right font-mono">{fmtMXN(j.revenue_mxn)}</td>
                                    <td className="p-3 text-right font-mono">{fmtMXN(j.amount_paid_mxn)}</td>
                                    <td className="p-3 text-right font-mono text-amber-300 font-bold">{fmtMXN(j.amount_outstanding_mxn)}</td>
                                    <td className="p-3 text-right">{j.days_since_start ?? "—"}</td>
                                    <td className="p-3 text-right">
                                        <button type="button" onClick={() => setPayJob(j)} data-testid={`mark-paid-${j.code}`}
                                            className="h-8 px-3 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold">
                                            {dict.finance.recv.markPaid}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {data.rows.length === 0 && (
                                <tr><td colSpan={8} className="px-0 py-0"><EmptyState icon={CheckCircle2} title={dict.finance.recv.empty} testid="recv-empty"/></td></tr>
                            )}
                        </tbody>
                    </table>
                </>
            )}
            {payJob && <MarkPaidModal job={payJob} onClose={() => setPayJob(null)} onSuccess={() => { setPayJob(null); reload(); }}/>}
        </div>
    );
}

function MarkPaidModal({ job, onClose, onSuccess }) {
    const [amount, setAmount] = useState(job.amount_outstanding_mxn);
    const [err, setErr] = useState("");
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        const totalPaid = (job.amount_paid_mxn || 0) + Number(amount);
        if (totalPaid > job.revenue_mxn + 0.01) { setErr(dict.finance.recv.errOverpay); return; }
        setSaving(true);
        try {
            await api.post(`/finance/jobs/${job.id}/payment`, { amount_mxn: totalPaid });
            onSuccess();
        } catch (e) { setErr(formatApiError(e)); }
        finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="markpaid-modal">
            <div className="rounded-2xl border border-slate-800 bg-slate-900 w-full max-w-md p-6">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                        <h2 className="font-display text-2xl font-bold">{dict.finance.recv.markPaidTitle}</h2>
                        <div className="text-sm text-slate-400 mt-1">{job.code} • {job.client_name}</div>
                    </div>
                    <button type="button" onClick={onClose} data-testid="markpaid-close" className="text-slate-500 hover:text-slate-100"><X size={20}/></button>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                    <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.finance.revenue}</div><div className="font-mono mt-1">{fmtMXN(job.revenue_mxn)}</div></div>
                    <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{dict.finance.client.paid}</div><div className="font-mono mt-1">{fmtMXN(job.amount_paid_mxn)}</div></div>
                    <div className="col-span-2"><div className="text-[10px] uppercase tracking-wider text-amber-300">{dict.finance.recv.outstanding}</div><div className="font-mono font-bold text-amber-300 mt-1">{fmtMXN(job.amount_outstanding_mxn)}</div></div>
                </div>
                <label className="block text-xs uppercase tracking-wider text-slate-400 mb-2">{dict.finance.recv.amountToPay}</label>
                <input type="number" step="0.01" min="0" max={job.amount_outstanding_mxn}
                    value={amount} onChange={e => setAmount(e.target.value)} data-testid="markpaid-amount"
                    className="w-full h-11 px-3 rounded-lg bg-slate-950 border border-slate-700 text-base font-mono"/>
                <div className="flex gap-2 mt-3 text-xs">
                    <button type="button" onClick={() => setAmount(job.amount_outstanding_mxn)} className="px-2 h-7 rounded border border-slate-700 hover:bg-slate-800">{dict.finance.recv.payFull}</button>
                    <button type="button" onClick={() => setAmount((job.amount_outstanding_mxn / 2).toFixed(2))} className="px-2 h-7 rounded border border-slate-700 hover:bg-slate-800">50%</button>
                </div>
                {err && <div className="mt-3 text-sm text-red-300" data-testid="markpaid-error">{err}</div>}
                <div className="flex gap-2 justify-end mt-5">
                    <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-700 hover:bg-slate-800 text-sm">{dict.common.cancel}</button>
                    <button type="button" onClick={submit} disabled={saving || !amount} data-testid="markpaid-confirm"
                        className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-2">
                        {saving ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>}
                        {dict.finance.recv.confirmPayment}
                    </button>
                </div>
            </div>
        </div>
    );
}

function CashflowTab({ range }) {
    const [data, setData] = useState(null); const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (!range.from || !range.to) return;
        setLoading(true);
        api.get(`/finance/cash-vs-financed?from=${range.from}&to=${range.to}`)
            .then(r => setData(r.data)).finally(() => setLoading(false));
    }, [range.from, range.to]);
    if (loading || !data) return <div className="text-center py-12"><Loader2 className="animate-spin inline text-slate-500"/></div>;
    return (
        <div className="space-y-5" data-testid="cashflow-tab">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Kpi testid="cash-inflow" label={dict.finance.cash.inflow} value={fmtMXN(data.cash_inflow_mxn)} accent="text-emerald-300"/>
                <Kpi testid="cash-outflow-op" label={dict.finance.cash.outflowOp} value={fmtMXN(data.cash_outflow_operational_mxn)} accent="text-rose-300"/>
                <Kpi testid="cash-outflow-fin" label={dict.finance.cash.outflowFin} value={fmtMXN(data.cash_outflow_financing_mxn)} accent="text-amber-300"/>
                <Kpi testid="cash-net" label={dict.finance.cash.net} value={fmtMXN(data.net_cash_mxn)} accent={data.net_cash_mxn >= 0 ? "text-emerald-300" : "text-rose-300"}/>
            </div>
            {data.financed_assets.length > 0 && (
                <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
                    <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2"><CreditCard size={18} className="text-brand-orange"/>{dict.finance.cash.financedAssets}</h2>
                    <table className="w-full text-sm">
                        <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500"><th className="text-left py-2">{dict.finance.cash.asset}</th><th className="text-left py-2">{dict.finance.cash.lender}</th><th className="text-right py-2">{dict.finance.cash.monthlyPayment}</th><th className="text-right py-2">{dict.finance.cash.remainingBalance}</th><th className="text-right py-2">{dict.finance.cash.monthsLeft}</th></tr></thead>
                        <tbody>
                            {data.financed_assets.map(a => (
                                <tr key={a.asset_id} className="border-t border-slate-800" data-testid={`fin-${a.asset_id}`}>
                                    <td className="py-2 font-semibold">{a.nickname}</td>
                                    <td className="py-2 text-slate-400">{a.lender || "—"}</td>
                                    <td className="py-2 text-right font-mono">{fmtMXN(a.monthly_payment_mxn)}</td>
                                    <td className="py-2 text-right font-mono text-brand-orange font-bold">{fmtMXN(a.remaining_balance_mxn)}</td>
                                    <td className="py-2 text-right text-xs text-slate-400" data-testid={`fin-months-${a.asset_id}`}>{a.months_remaining} {dict.finance.cash.months}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const TABS_BY_ROLE = {
    admin: ["resumen", "asset", "operator", "client", "receivables", "cashflow"],
    manager: ["resumen", "asset", "client", "receivables"],
};

export default function Finanzas() {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const allowed = TABS_BY_ROLE[user?.role] || [];
    const initialTab = allowed.includes(searchParams.get("tab")) ? searchParams.get("tab") : allowed[0];
    const [tab, setTabState] = useState(initialTab);
    const [range, setRange] = useState({ from: "", to: "" });
    if (user?.role === "operator") return <Navigate to="/campo" replace/>;
    const setTab = (t) => { setTabState(t); const sp = new URLSearchParams(searchParams); sp.set("tab", t); setSearchParams(sp, { replace: true }); };
    const tabsMeta = [
        { k: "resumen", l: dict.finance.tabs.summary, icon: TrendingUp },
        { k: "asset", l: dict.finance.tabs.byAsset, icon: Briefcase },
        { k: "operator", l: dict.finance.tabs.byOperator, icon: HardHat },
        { k: "client", l: dict.finance.tabs.byClient, icon: Users2 },
        { k: "receivables", l: dict.finance.tabs.receivables, icon: AlertCircle },
        { k: "cashflow", l: dict.finance.tabs.cashflow, icon: ArrowDownToLine },
    ].filter(m => allowed.includes(m.k));

    return (
        <div className="space-y-6" data-testid="finanzas-page">
            <header className="flex items-baseline justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">{dict.finance.title}</h1>
                    <p className="mt-2 text-slate-400 text-sm">{dict.finance.subtitle}</p>
                </div>
                <DateRangeBar value={range} onChange={setRange}/>
            </header>

            <div className="flex gap-1 border-b border-slate-800 overflow-x-auto">
                {tabsMeta.map(m => (
                    <button key={m.k} type="button" onClick={() => setTab(m.k)} data-testid={`fin-tab-${m.k}`}
                        className={`-mb-px shrink-0 px-4 h-10 text-xs font-semibold uppercase tracking-wider border-b-2 transition inline-flex items-center gap-2 ${tab === m.k ? "border-brand-orange text-brand-orange" : "border-transparent text-slate-400 hover:text-slate-100"}`}>
                        <m.icon size={14}/>{m.l}
                    </button>
                ))}
            </div>

            {tab === "resumen" && <ResumenTab range={range}/>}
            {tab === "asset" && <AssetTab range={range}/>}
            {tab === "operator" && <OperatorTab range={range}/>}
            {tab === "client" && <ClientTab range={range}/>}
            {tab === "receivables" && <ReceivablesTab/>}
            {tab === "cashflow" && <CashflowTab range={range}/>}
        </div>
    );
}
